// tonviewer link construction. The backend ships a trade's `tx_hash` as STANDARD
// base64 (alphabet +,/ with = padding — e.g. "NXpRSTIUB/1TOAAYqvG24..="). Feeding
// that straight into tonviewer's /transaction/<hash> route — whether raw or
// percent-encoded — does NOT resolve: tonviewer wants the 64-char hex form. So we
// decode the base64 to bytes and re-encode as hex. base64url (-,_ , no padding) is
// tolerated too. A TON tx hash is exactly 32 bytes, so anything that does not decode
// to 32 bytes is rejected rather than silently turned into a bogus short "hex".

const TONVIEWER_TX = 'https://tonviewer.com/transaction/'

// txHashToHex converts a base64 / base64url tx hash to its 64-char lowercase hex form.
// Returns null when the input is not a valid 32-byte hash (undecodable, or decodes to
// the wrong length) — the caller decides how to degrade. An already-64-char hex string
// is accepted and lowercased.
export function txHashToHex(hash: string): string | null {
  const s = hash.trim()
  if (/^[0-9a-fA-F]{64}$/.test(s)) return s.toLowerCase()
  try {
    let b64 = s.replace(/-/g, '+').replace(/_/g, '/')
    // Re-pad to a multiple of 4 so base64url-without-padding decodes; a remainder of 1
    // is not valid base64.
    const rem = b64.length % 4
    if (rem === 1) return null
    if (rem === 2) b64 += '=='
    else if (rem === 3) b64 += '='
    const bin = atob(b64)
    if (bin.length !== 32) return null // a TON tx hash is exactly 32 bytes
    let hex = ''
    for (let i = 0; i < bin.length; i++) {
      hex += bin.charCodeAt(i).toString(16).padStart(2, '0')
    }
    return hex
  } catch {
    return null
  }
}

// txHashToTonviewer returns the full tonviewer transaction URL for a tx hash. For any
// real 32-byte hash the path is clean lowercase hex (no base64 specials, no %). The
// encodeURIComponent branch is a defensive last resort for malformed input that never
// occurs for a real backend hash — it keeps a stray +,/,= from breaking the URL path.
export function txHashToTonviewer(hash: string): string {
  const hex = txHashToHex(hash)
  return `${TONVIEWER_TX}${hex ?? encodeURIComponent(hash)}`
}
